import { useMemo } from 'react';

import { useChartDataDisplay } from 'hooks/helperHooks';
import { pitchTypes } from 'utilities/constants';
import { pitchTypeToColor, pitchTypeGroupToColor } from 'utilities/colorHelpers';
import { GraphicalLineChartDataset } from 'components/LineChart/LineChart';
import { LineChartDataset, TimelineChartDataset } from 'types/CodegenReportTypes';

const simulateLineChartData = (isLoading: boolean): LineChartDataset[] => {
  const chartData = pitchTypes
    .filter((pitchType) => pitchType !== 'Undefined')
    .map((pitchType) => ({
      label: pitchType,
      points: !isLoading
        ? []
        : Array(10)
            .fill(0)
            .map((_, i) => ({
              x: i + 1,
              y: Math.random() * 100,
            })),
    }));

  return chartData;
};

const simulateTimelineChartData = (isLoading: boolean): TimelineChartDataset[] => {
  const currentYear = new Date().getFullYear();
  const chartData = pitchTypes
    .filter((pitchType) => pitchType !== 'Undefined')
    .map((pitchType) => ({
      label: pitchType,
      points: !isLoading
        ? []
        : [
            `${currentYear}-01-01`,
            `${currentYear}-02-01`,
            `${currentYear}-03-01`,
            `${currentYear}-04-01`,
            `${currentYear}-05-01`,
            `${currentYear}-06-01`,
            `${currentYear}-07-01`,
            `${currentYear}-08-01`,
          ].map((date) => ({
            x: date,
            y: Math.random() * 100,
          })),
    }));

  return chartData;
};

export const useLineChartData = (chartData: LineChartDataset[] | undefined, isLoading: boolean): GraphicalLineChartDataset[] => {
  const chartDataDisplay = useChartDataDisplay(chartData, simulateLineChartData, isLoading);

  const mappedChartData: GraphicalLineChartDataset[] = useMemo(
    () =>
      chartDataDisplay.map((data) => ({
        label: data.label,
        color: pitchTypeToColor[data.label] ?? pitchTypeGroupToColor[data.label],
        points: data.points,
        drawMode: 'groupColor',
      })),
    [chartDataDisplay]
  );

  return mappedChartData;
};

export const useTimelineChartData = (chartData: TimelineChartDataset[] | undefined, isLoading: boolean): GraphicalLineChartDataset[] => {
  const chartDataDisplay = useChartDataDisplay(chartData, simulateTimelineChartData, isLoading);

  const mappedChartData: GraphicalLineChartDataset[] = useMemo(
    () =>
      chartDataDisplay.map((data) => ({
        label: data.label,
        color: pitchTypeToColor[data.label] ?? pitchTypeGroupToColor[data.label],
        points: data.points,
        drawMode: 'groupColor',
      })),
    [chartDataDisplay]
  );

  return mappedChartData;
};
